// Utility functions for handling product images

// Cache for blob URLs to prevent recreation
const blobUrlCache = new Map();

/**
 * Clear the blob URL cache
 */
export const clearBlobUrlCache = () => {blobUrlCache.clear();
};

// Global cleanup on page unload (not on navigation)
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {blobUrlCache.clear();
  });
}

/**
 * Get the best available image for a product
 * @param {Object} product - The product object
 * @returns {string} - The best available image URL
 */
// Helper function to check if a URL is valid (not a relative path like /assets/)
const isValidImageUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  // Valid URLs: http, https, blob, data URLs
  if (url.startsWith('http://') || url.startsWith('https://') || 
      url.startsWith('blob:') || url.startsWith('data:')) {
    return true;
  }
  // Relative paths starting with /assets/ are invalid (they don't exist in public folder)
  if (url.startsWith('/assets/')) {
    return false;
  }
  // Other relative paths might be valid (like /logo.png)
  return true;
};

export const getProductImage = (product) => {
  if (!product) {
    return null;
  }
  
  // Handle base64 data first (highest priority)
  if (product.base64Data) {
    // Ensure base64Data is a valid string
    if (typeof product.base64Data === 'string' && product.base64Data.length > 0) {
      // CRITICAL FIX: Use data URLs directly instead of blob URLs
      // Data URLs are more reliable and don't get revoked like blob URLs
      // If base64Data already starts with "data:", use it directly
      if (product.base64Data.startsWith('data:image')) {
        return product.base64Data;
      }
      
      // If base64Data is just the base64 string without prefix, create a data URL
      // Default to JPEG if we can't determine the type
      const dataUrl = `data:image/jpeg;base64,${product.base64Data}`;
      return dataUrl;
    }
  }
  
  // Check legacy img field (for backward compatibility)
  // But only if it's not a blob URL or invalid relative path
  if (product.img && !product.img.startsWith('blob:') && isValidImageUrl(product.img)) {
    return product.img;
  }
  
  // Check primary image from Product Management
  // But only if it's not a blob URL or invalid relative path
  if (product.imageUrl && !product.imageUrl.startsWith('blob:') && isValidImageUrl(product.imageUrl)) {
    return product.imageUrl;
  }
  
  // Check carousel images from Product Management
  if (product.images && Array.isArray(product.images) && product.images.length > 0) {
    const firstImage = product.images[0];
    if (typeof firstImage === 'string' && !firstImage.startsWith('blob:') && isValidImageUrl(firstImage)) {
      return firstImage;
    }
    if (firstImage && typeof firstImage === 'object') {
      // Priority: base64Data > valid imageUrl
      if (firstImage.base64Data) {
        // CRITICAL FIX: Use data URLs directly instead of blob URLs
        // If base64Data already starts with "data:", use it directly
        if (firstImage.base64Data.startsWith('data:image')) {
          return firstImage.base64Data;
        }
        // Otherwise create a data URL
        return `data:image/jpeg;base64,${firstImage.base64Data}`;
      }
      if (firstImage.imageUrl && !firstImage.imageUrl.startsWith('blob:') && isValidImageUrl(firstImage.imageUrl)) {
        return firstImage.imageUrl;
      }
    }
  }
  
  // No valid images foundreturn null;
};

/**
 * Get all available images for a product
 * @param {Object} product - The product object
 * @returns {Array} - Array of image URLs
 */
export const getProductImages = (product) => {
  if (!product) return [];
  
  let images = [];
  
  // Check carousel images from Product Management
  if (product.images && Array.isArray(product.images) && product.images.length > 0) {
    // Process carousel images from Product Management
    images = product.images.map(img => {
      // Priority: base64Data > valid URLs
      if (img && typeof img === 'object' && img.base64Data) {
        // CRITICAL FIX: Use data URLs directly instead of blob URLs
        if (img.base64Data.startsWith('data:image')) {
          return img.base64Data;
        }
        // Otherwise create a data URL
        const dataUrl = `data:image/jpeg;base64,${img.base64Data}`;
        if (dataUrl) return dataUrl;
        // Fallback to imageUrl if base64 fails
        if (img.imageUrl && isValidImageUrl(img.imageUrl)) return img.imageUrl;
        return null;
      }
      if (typeof img === 'string' && !img.startsWith('blob:') && isValidImageUrl(img)) {
        return img;
      }
      if (img && typeof img === 'object') {
        if (img.imageUrl && !img.imageUrl.startsWith('blob:') && isValidImageUrl(img.imageUrl)) {
          return img.imageUrl;
        }
        if (img.url && !img.url.startsWith('blob:') && isValidImageUrl(img.url)) {
          return img.url;
        }
      }
      return null;
    }).filter(Boolean);
  }
  // Check for base64 data (highest priority for primary image)
  else if (product.base64Data) {
    // CRITICAL FIX: Use data URLs directly instead of blob URLs
    if (product.base64Data.startsWith('data:image')) {
      images = [product.base64Data];
    } else {
      images = [`data:image/jpeg;base64,${product.base64Data}`];
    }
  }
  // Check for primary image from Product Management
  else if (product.imageUrl && !product.imageUrl.startsWith('blob:') && isValidImageUrl(product.imageUrl)) {
    images = [product.imageUrl];
  }
  // Check for legacy img field
  else if (product.img && !product.img.startsWith('blob:') && isValidImageUrl(product.img)) {
    images = [product.img];
  }
  
  return images;
};

/**
 * Process product data to ensure consistent image structure
 * @param {Object} product - The product object
 * @returns {Object} - Product with normalized image data
 */
export const normalizeProductImages = (product) => {
  if (!product) return product;
  
  // Get the primary image, but don't store blob URLs in normalized data
  let primaryImage = null;
  
  // Check for base64 data first (highest priority)
  // This is especially important for minimized cart items
  if (product.base64Data) {
    // Ensure base64Data is a valid string
    if (typeof product.base64Data === 'string' && product.base64Data.length > 0) {
      // CRITICAL FIX: Use data URLs directly instead of blob URLs
      if (product.base64Data.startsWith('data:image')) {
        primaryImage = product.base64Data;
      } else {
        primaryImage = `data:image/jpeg;base64,${product.base64Data}`;
      }
    }
  }
  // Check for non-blob URLs that are valid (not invalid relative paths)
  // But only if we don't have base64Data (base64Data takes priority)
  if (!primaryImage && product.imageUrl && !product.imageUrl.startsWith('blob:') && isValidImageUrl(product.imageUrl)) {
    primaryImage = product.imageUrl;
  }
  if (!primaryImage && product.img && !product.img.startsWith('blob:') && isValidImageUrl(product.img)) {
    primaryImage = product.img;
  }
  // Check carousel images for non-blob URLs
  if (!primaryImage && product.images && Array.isArray(product.images) && product.images.length > 0) {
    const firstImage = product.images[0];
    if (typeof firstImage === 'string' && !firstImage.startsWith('blob:') && isValidImageUrl(firstImage)) {
      primaryImage = firstImage;
    } else if (firstImage && typeof firstImage === 'object') {
      if (firstImage.base64Data) {
        // CRITICAL FIX: Use data URLs directly instead of blob URLs
        if (firstImage.base64Data.startsWith('data:image')) {
          primaryImage = firstImage.base64Data;
        } else {
          primaryImage = `data:image/jpeg;base64,${firstImage.base64Data}`;
        }
      } else if (firstImage.imageUrl && !firstImage.imageUrl.startsWith('blob:') && isValidImageUrl(firstImage.imageUrl)) {
        primaryImage = firstImage.imageUrl;
      }
    }
  }
  
  // Get all images, creating fresh blob URLs from base64 data
  const images = getProductImages(product);
  
  return {
    ...product,
    img: primaryImage, // Legacy field for compatibility
    imageUrl: primaryImage,
    images: images.length > 0 ? images : (primaryImage ? [primaryImage] : []),
    // Preserve base64Data if it exists (this is the source of truth for getProductImage)
    // This is critical for minimized cart items
    base64Data: product.base64Data
  };
};

/**
 * Handle image loading errors
 * @param {Event} event - The error event
 */
export const handleImageError = (event) => {
  console.warn('Image failed to load:', event.target.src);
  
  // Try to find a fallback image or show placeholder
  const img = event.target;
  const parent = img.parentElement;
  
  // If it's a blob URL that failed, try to recreate it
  if (img.src && img.src.startsWith('blob:')) {// Try to find the product data from the parent element
    const productData = parent.getAttribute('data-product');
    if (productData) {
      try {
        const product = JSON.parse(productData);
        const newImageUrl = getProductImage(product);
        if (newImageUrl && newImageUrl !== img.src) {img.src = newImageUrl;
          return; // Don't show placeholder yet, let it try to load
        }
      } catch (error) {
        console.warn('Failed to parse product data for image recreation:', error);
      }
    }
  }
  
  if (parent) {
    // Check if there's already a placeholder
    const existingPlaceholder = parent.querySelector('.no-image-placeholder');
    if (!existingPlaceholder) {
      // Create a placeholder div
      const placeholder = document.createElement('div');
      placeholder.className = 'no-image-placeholder';
      placeholder.innerHTML = '<span>No Image</span>';
      
      // Replace the broken image with placeholder
      parent.replaceChild(placeholder, img);
    } else {
      // Just hide the broken image
      img.style.display = 'none';
    }
  } else {
    // Fallback: just hide the image
    img.style.display = 'none';
  }
};
